
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../utils/axiosInstance";
import Select, { selectTexts } from "./ui/Select";
import { iranCustoms } from "../constants/iranCustoms";
import { countries } from "../constants/countryList";
import { fetchOrders } from "../actions/performaActions";
import toast from "react-hot-toast";
import { Save, PlusCircle, Trash2 } from "lucide-react";
import InvoicePartyQuickCreate from "./InvoicePartyQuickCreate";
import { getApiErrorLines } from "../utils/formErrors";
import { ERROR_TOAST_OPTIONS, formatErrorLines, toastErrors } from "../utils/toastErrors";
import { API_ERROR_LABELS, validateInvoice } from "../utils/invoiceValidation";
import { toLatinDigits } from "../utils/numberFormat";

const BASE_CURRENCY_OPTIONS = [
  { value: "USD", label: "دلار آمریکا", aliases: ["دلار", "دلار امریکا"] },
  { value: "EUR", label: "یورو", aliases: [] },
  { value: "CNY", label: "یوان چین", aliases: ["یوان"] },
  { value: "GBP", label: "پوند استرلینگ", aliases: ["پوند"] },
  { value: "IRR", label: "ریال", aliases: [] },
  {
    value: "AED",
    label: "درهم امارات",
    aliases: ["درهم", "درهم امارات متحده عربی"],
  },
];

const resolveCurrencyValue = (currency) => {
  const normalized = String(currency || "").trim().toLocaleLowerCase();
  if (!normalized) return "";

  const option = BASE_CURRENCY_OPTIONS.find((item) =>
    [item.value, item.label, ...item.aliases].some(
      (value) => String(value).trim().toLocaleLowerCase() === normalized,
    ),
  );
  return option?.value || String(currency).trim();
};

const getInvoiceApiError = (error) =>
  formatErrorLines(
    getApiErrorLines(error, API_ERROR_LABELS, "ایجاد فاکتور با خطا مواجه شد."),
  );

const FieldLabel = ({ children, required = false, optional = false }) => (
  <label className="text-sm text-gray-600">
    {children}
    {required ? <span className="mr-0.5 text-red-500">*</span> : null}
    {optional ? <span className="mr-1 text-xs text-gray-400">(اختیاری)</span> : null}
  </label>
);

export default function InvoiceForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orders = useSelector((state) => state.order?.orders) ?? [];

  // Fetch sellers, buyers, cottages
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [cottages, setCottages] = useState([]);
  const [quickCreateType, setQuickCreateType] = useState(null);

  useEffect(() => {
    dispatch(fetchOrders(1, 600));
    axiosInstance
      .get("cottages/numbers/", { params: { page_size: 600 } })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.results)
          ? res.data.results
          : [];
        setCottages(list);
      })
      .catch(() => toast.error("دریافت فهرست اظهارنامه‌ها ناموفق بود."));

    axiosInstance
      .get("documents/sellers/")
      .then((res) => setSellers(res.data))
      .catch(() => toast.error("دریافت فهرست فروشندگان ناموفق بود."));

    axiosInstance
      .get("documents/buyers/")
      .then((res) => setBuyers(res.data))
      .catch(() => toast.error("دریافت فهرست خریداران ناموفق بود."));
  }, [dispatch]);

  // Options
  const sellerOptions = sellers.map((sel) => ({
    value: sel.id,
    label: sel.seller_name,
  }));
  const buyerOptions = buyers.map((buyer) => ({
    value: buyer.id,
    label: buyer.buyer_name,
  }));
  const proformaOptions = orders.map((o) => ({
    value: o.prfVCodeInt,
    label: o.prf_order_no,
  }));
  const meansOfTransportOptions = [
    { value: "By Truck", label: "By Truck" },
    { value: "By AirPlane", label: "By AirPlane" },
    { value: "By Vessel", label: "By Vessel" },
    { value: "By Train", label: "By Train" },
  ];
  const termsOfDeliveryOptions = [
    { value: "CFR", label: "CFR" },
    { value: "CIF", label: "CIF" },
    { value: "CIP", label: "CIP" },
    { value: "CPT", label: "CPT" },
    { value: "DAP", label: "DAP" },
    { value: "DDP", label: "DDP" },
    { value: "DPU", label: "DPU" },
    { value: "EXW", label: "EXW" },
    { value: "FAS", label: "FAS" },
    { value: "FCA", label: "FCA" },
    { value: "FOB", label: "FOB" },
  ];
  const unitOptions = [
    { value: "KG", label: "KG" },
    { value: "M3", label: "M3" },
    { value: "M2", label: "M2" },
    { value: "M", label: "M" },
    { value: "PCS", label: "PCS" },
    { value: "SET", label: "SET" },
  ];
  const iranCustomsOptions = iranCustoms.map((custom) => ({
    value: custom.ctmNameStr,
    label: `${custom.ctmNameStr} (${custom.ctmVCodeInt})`,
  }));
  const countryOptions = countries.map((country) => ({
    value: country.name,
    label: `${country.name} (${country.persianName})`,
  }));

  // Form state
  const [invoiceData, setInvoiceData] = useState({
    seller: "",
    buyer: "",
    cottage: "",
    proforma: "",
    invoice_number: "",
    invoice_currency: "AED",
    freight_charges: 0,
    terms_of_delivery: "CPT",
    partial_shipment: false,
    relevant_location: "",
    means_of_transport: "By Vessel",
    country_of_origin: "",
    port_of_loading: "",
    customer_tel: "",
    invoice_date: "",
    items: [
      {
        description: "",
        quantity: 1,
        unit_price: 0,
        nw_kg: 0,
        gw_kg: 0,
        unit: "PCS",
        commodity_code: "",
        pack: 1,
        origin: "",
      },
    ],
  });

  const currencyOptions = useMemo(() => {
    const options = BASE_CURRENCY_OPTIONS.map(({ aliases, ...option }) => option);
    const knownValues = new Set(options.map((option) => option.value));

    cottages.forEach((cottage) => {
      const value = resolveCurrencyValue(cottage.proforma_currency_type);
      if (value && !knownValues.has(value)) {
        options.push({ value, label: value });
        knownValues.add(value);
      }
    });
    return options;
  }, [cottages]);

  const cottageOptions = useMemo(() => {
    if (!invoiceData.proforma) return [];

    return cottages
      .filter(
        (cottage) =>
          String(cottage.proforma_vcode) === String(invoiceData.proforma),
      )
      .map((cottage) => ({
        value: cottage.cottage_number,
        label: cottage.has_invoice
          ? `${cottage.cottage_number} - دارای فاکتور`
          : String(cottage.cottage_number),
        isDisabled: Boolean(cottage.has_invoice),
        currency: resolveCurrencyValue(cottage.proforma_currency_type),
      }));
  }, [cottages, invoiceData.proforma]);

  // Derived totals (for a small live summary card)
  const { subTotal, totalQty, totalNW, totalGW, totalPack } = useMemo(() => {
    const subTotal = invoiceData.items.reduce(
      (acc, it) =>
        acc + (parseFloat(it.unit_price) || 0) * (parseFloat(it.quantity) || 0),
      0
    );
    const totalQty = invoiceData.items.reduce(
      (a, it) => a + (parseFloat(it.quantity) || 0),
      0
    );
    const totalNW = invoiceData.items.reduce(
      (a, it) => a + (parseFloat(it.nw_kg) || 0),
      0
    );
    const totalGW = invoiceData.items.reduce(
      (a, it) => a + (parseFloat(it.gw_kg) || 0),
      0
    );
    const totalPack = invoiceData.items.reduce(
      (a, it) => a + (parseFloat(it.pack) || 0),
      0
    );
    return { subTotal, totalQty, totalNW, totalGW, totalPack };
  }, [invoiceData.items]);

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceData((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        [name]: type === "checkbox" ? checked : value,
      };
      return { ...prev, items };
    });
  };

  const addItemRow = () => {
    setInvoiceData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: "",
          quantity: 1,
          unit_price: 0,
          nw_kg: 0,
          gw_kg: 0,
          unit: "PCS",
          commodity_code: "",
          pack: 1,
          origin: "",
        },
      ],
    }));
  };

  const removeItemRow = (index) => {
    setInvoiceData((prev) => {
      const items = [...prev.items];
      items.splice(index, 1);
      return { ...prev, items };
    });
  };

  const handlePartyCreated = (party) => {
    if (quickCreateType === "seller") {
      setSellers((previous) => [...previous, party]);
      setInvoiceData((previous) => ({ ...previous, seller: party.id }));
      return;
    }

    setBuyers((previous) => [...previous, party]);
    setInvoiceData((previous) => ({ ...previous, buyer: party.id }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const problems = validateInvoice(invoiceData);
    if (problems.length) {
      toastErrors(problems);
      return;
    }

    // Proforma and cottage are optional; send null rather than "".
    const payload = {
      ...invoiceData,
      proforma: invoiceData.proforma || null,
      cottage: invoiceData.cottage || null,
      freight_charges: parseFloat(invoiceData.freight_charges) || 0,
      items: invoiceData.items.map((item) => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        nw_kg: parseFloat(item.nw_kg) || 0,
        gw_kg: parseFloat(item.gw_kg) || 0,
        commodity_code: item.commodity_code,
        pack: parseInt(item.pack, 10) || 0,
        unit: item.unit,
        origin: item.origin,
      })),
    };

    try {
      await toast.promise(
        axiosInstance.post("documents/invoices/", payload),
        {
          loading: "در حال ایجاد فاکتور…",
          success: "فاکتور با موفقیت ایجاد شد.",
          error: getInvoiceApiError,
        },
        { error: ERROR_TOAST_OPTIONS },
      );
      navigate("/invoices/list");
    } catch {
      // toast.promise displays the backend validation message.
    }
  };

  // react-select styling (matches the modern cards)
  const selectStyles = {
    control: (p) => ({
      ...p,
      fontFamily: "Vazirmatn, sans-serif",
      direction: "rtl",
      borderColor: "#E5E7EB",
      borderRadius: "0.75rem",
      paddingBlock: "2px",
      boxShadow: "none",
      backgroundColor: "#FFFFFF",
      minHeight: "42px",
      ":hover": { borderColor: "#2563EB" },
    }),
    placeholder: (p) => ({ ...p, color: "#9CA3AF", textAlign: "right" }),
    singleValue: (p) => ({ ...p, color: "#111827", textAlign: "right" }),
    multiValue: (p) => ({
      ...p,
      backgroundColor: "#EEF2FF",
      borderRadius: "0.5rem",
    }),
    multiValueLabel: (p) => ({ ...p, color: "#111827" }),
    multiValueRemove: (p) => ({
      ...p,
      ":hover": { backgroundColor: "#2563EB", color: "#fff" },
    }),
    menu: (p) => ({
      ...p,
      fontFamily: "Vazirmatn, sans-serif",
      direction: "rtl",
      borderRadius: "0.75rem",
      overflow: "hidden",
    }),
    option: (p, state) => ({
      ...p,
      textAlign: "right",
      backgroundColor: state.isSelected
        ? "#2563EB"
        : state.isFocused
        ? "#DBEAFE"
        : "#fff",
      color: state.isSelected ? "#fff" : "#111827",
    }),
  };

  return (
    <>
    <form
      dir="rtl"
      onSubmit={handleSubmit}
      noValidate
      className="font-vazir mt-20 md:mt-24 px-3"
    >
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold">ایجاد فاکتور</h2>
          <p className="text-sm text-gray-500 mt-1">
            اطلاعات پایه و اقلام فاکتور را تکمیل کنید.
          </p>
        </div>
      </div>

      {/* Card: Basic info */}
      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-4 md:px-6 py-4">
          <h3 className="text-base font-semibold">اطلاعات پایه</h3>
        </div>
        <div className="px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Proforma */}
            <div className="space-y-1">
              <FieldLabel optional>ثبت سفارش</FieldLabel>
              <Select
                options={proformaOptions}
                value={
                  proformaOptions.find(
                    (o) => o.value === invoiceData.proforma
                  ) || null
                }
                onChange={(opt) =>
                  setInvoiceData((p) => ({
                    ...p,
                    proforma: opt ? opt.value : "",
                    cottage: "",
                  }))
                }
                placeholder="انتخاب ثبت سفارش"
                {...selectTexts("ثبت سفارشی یافت نشد.")}
                styles={selectStyles}
                isClearable
              />
            </div>

            {/* Cottage */}
            <div className="space-y-1">
              <FieldLabel optional>اظهارنامه</FieldLabel>
              <Select
                options={cottageOptions}
                value={
                  cottageOptions.find((o) => o.value === invoiceData.cottage) ||
                  null
                }
                onChange={(opt) => {
                  if (opt?.isDisabled) {
                    toast.error("برای این کوتاژ قبلاً فاکتور ثبت شده است.");
                    return;
                  }
                  setInvoiceData((p) => ({
                    ...p,
                    cottage: opt ? opt.value : "",
                    invoice_currency: opt?.currency || p.invoice_currency,
                  }));
                }}
                isOptionDisabled={(option) => option.isDisabled}
                isDisabled={!invoiceData.proforma}
                placeholder={
                  invoiceData.proforma
                    ? "انتخاب اظهارنامه"
                    : "برای انتخاب، ابتدا ثبت سفارش را انتخاب کنید"
                }
                {...selectTexts("برای این ثبت سفارش اظهارنامه‌ای ثبت نشده است.")}
                styles={selectStyles}
                isClearable
              />
              <p className="text-xs text-gray-500">
                {invoiceData.proforma
                  ? "فقط کوتاژهای همین ثبت سفارش نمایش داده می‌شوند."
                  : "فاکتور را بدون ثبت سفارش و اظهارنامه هم می‌توانید ثبت کنید."}
              </p>
            </div>

            {/* Seller */}
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel required>فروشنده</FieldLabel>
                <button
                  type="button"
                  onClick={() => setQuickCreateType("seller")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  <PlusCircle className="h-4 w-4" />
                  فروشنده جدید
                </button>
              </div>
              <Select
                options={sellerOptions}
                value={
                  sellerOptions.find((o) => o.value === invoiceData.seller) ||
                  null
                }
                onChange={(opt) =>
                  setInvoiceData((p) => ({
                    ...p,
                    seller: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب فروشنده"
                {...selectTexts("هنوز فروشنده‌ای ثبت نشده است؛ از «فروشنده جدید» استفاده کنید.")}
                styles={selectStyles}
                isClearable
              />
            </div>

            {/* Buyer */}
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel required>خریدار</FieldLabel>
                <button
                  type="button"
                  onClick={() => setQuickCreateType("buyer")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  <PlusCircle className="h-4 w-4" />
                  خریدار جدید
                </button>
              </div>
              <Select
                options={buyerOptions}
                value={
                  buyerOptions.find((o) => o.value === invoiceData.buyer) ||
                  null
                }
                onChange={(opt) =>
                  setInvoiceData((p) => ({ ...p, buyer: opt ? opt.value : "" }))
                }
                placeholder="انتخاب خریدار"
                {...selectTexts("هنوز خریداری ثبت نشده است؛ از «خریدار جدید» استفاده کنید.")}
                styles={selectStyles}
                isClearable
              />
            </div>

            {/* Invoice number */}
            <div className="space-y-1">
              <FieldLabel required>شماره فاکتور</FieldLabel>
              <input
                name="invoice_number"
                value={invoiceData.invoice_number}
                onChange={handleChange}
                placeholder="شماره فاکتور"
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              />
            </div>

            {/* Invoice date */}
            <div className="space-y-1">
              <FieldLabel required>تاریخ فاکتور</FieldLabel>
              <input
                type="date"
                name="invoice_date"
                value={invoiceData.invoice_date}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              />
            </div>

            {/* Currency */}
            <div className="space-y-1">
              <FieldLabel required>ارز</FieldLabel>
              <Select
                options={currencyOptions}
                value={
                  currencyOptions.find(
                    (o) => o.value === invoiceData.invoice_currency
                  ) || null
                }
                onChange={(opt) =>
                  setInvoiceData((p) => ({
                    ...p,
                    invoice_currency: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب ارز"
                {...selectTexts()}
                styles={selectStyles}
                isClearable
              />
            </div>

            {/* Freight */}
            <div className="space-y-1">
              <FieldLabel>هزینه حمل</FieldLabel>
              <input
                type="number"
                name="freight_charges"
                value={invoiceData.freight_charges}
                onChange={handleChange}
                placeholder="هزینه حمل"
                onWheel={(e) => e.target.blur()}
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {/* Country of origin (multi) */}
            <div className="space-y-1">
              <FieldLabel required>کشور مبدأ</FieldLabel>
              <Select
                isMulti
                options={countryOptions}
                value={
                  invoiceData.country_of_origin
                    ? invoiceData.country_of_origin
                        .split("-")
                        .map((v) =>
                          countryOptions.find((o) => o.value === v.trim())
                        )
                    : []
                }
                onChange={(opts) => {
                  const vals = opts ? opts.map((o) => o.value) : [];
                  setInvoiceData((p) => ({
                    ...p,
                    country_of_origin: vals.join("-"),
                  }));
                }}
                placeholder="انتخاب کشور مبدأ"
                {...selectTexts()}
                styles={selectStyles}
              />
            </div>

            {/* Customer tel */}
            <div className="space-y-1">
              <FieldLabel>شماره مشتری</FieldLabel>
              <input
                name="customer_tel"
                value={invoiceData.customer_tel}
                onChange={handleChange}
                placeholder="شماره مشتری"
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {/* Port of loading (multi) */}
            <div className="space-y-1">
              <FieldLabel required>بندر بارگیری</FieldLabel>
              <Select
                isMulti
                options={countryOptions}
                value={
                  invoiceData.port_of_loading
                    ? invoiceData.port_of_loading
                        .split("-")
                        .map((v) =>
                          countryOptions.find((o) => o.value === v.trim())
                        )
                    : []
                }
                onChange={(opts) => {
                  const vals = opts ? opts.map((o) => o.value) : [];
                  setInvoiceData((p) => ({
                    ...p,
                    port_of_loading: vals.join("-"),
                  }));
                }}
                placeholder="انتخاب بندر بارگیری"
                {...selectTexts()}
                styles={selectStyles}
              />
            </div>

            {/* Terms of delivery */}
            <div className="space-y-1">
              <FieldLabel required>شرایط تحویل</FieldLabel>
              <Select
                options={termsOfDeliveryOptions}
                value={
                  termsOfDeliveryOptions.find(
                    (o) => o.value === invoiceData.terms_of_delivery
                  ) || null
                }
                onChange={(opt) =>
                  setInvoiceData((p) => ({
                    ...p,
                    terms_of_delivery: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب شرایط تحویل"
                {...selectTexts()}
                styles={selectStyles}
                isClearable
              />
            </div>

            {/* Means of transport (multi) */}
            <div className="space-y-1">
              <FieldLabel required>وسیله حمل</FieldLabel>
              <Select
                isMulti
                options={meansOfTransportOptions}
                value={
                  invoiceData.means_of_transport
                    ? invoiceData.means_of_transport
                        .split("-")
                        .map((v) =>
                          meansOfTransportOptions.find(
                            (o) => o.value === v.trim()
                          )
                        )
                    : []
                }
                onChange={(opts) => {
                  const vals = opts ? opts.map((o) => o.value) : [];
                  setInvoiceData((p) => ({
                    ...p,
                    means_of_transport: vals.join("-"),
                  }));
                }}
                placeholder="انتخاب وسیله حمل"
                {...selectTexts()}
                styles={selectStyles}
              />
            </div>

            {/* Relevant location (iran customs) */}
            <div className="space-y-1">
              <FieldLabel required>گمرک مقصد</FieldLabel>
              <Select
                isMulti
                options={iranCustomsOptions}
                value={
                  invoiceData.relevant_location
                    ? invoiceData.relevant_location
                        .split("-")
                        .map((v) =>
                          iranCustomsOptions.find((o) => o.value === v.trim())
                        )
                    : []
                }
                onChange={(opts) => {
                  const vals = opts ? opts.map((o) => o.value) : [];
                  setInvoiceData((p) => ({
                    ...p,
                    relevant_location: vals.join("-"),
                  }));
                }}
                placeholder="انتخاب گمرک مقصد"
                {...selectTexts()}
                styles={selectStyles}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden mt-6">
        <div className="border-b px-4 md:px-6 py-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">کالاها</h3>
          <button
            type="button"
            onClick={addItemRow}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            <PlusCircle className="w-4 h-4" />
            افزودن کالا
          </button>
        </div>

        <div className="px-4 md:px-6 py-6 space-y-4">
          {invoiceData.items.map((item, index) => (
            <div key={index} className="rounded-xl border p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 md:col-span-2">
                  <FieldLabel required>شرح کالا</FieldLabel>
                  <textarea
                    name="description"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, e)}
                    placeholder="شرح کالا"
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel required>تعداد</FieldLabel>
                  <input
                    type="number"
                    name="quantity"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, e)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel required>قیمت واحد</FieldLabel>
                  <input
                    type="number"
                    step="0.01"
                    name="unit_price"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, e)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel required>کد کالا (HS)</FieldLabel>
                  {/* Text, not number: HS codes can start with 0. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    dir="ltr"
                    maxLength={9}
                    name="commodity_code"
                    value={item.commodity_code}
                    onChange={(e) =>
                      handleItemChange(index, {
                        target: {
                          name: "commodity_code",
                          value: toLatinDigits(e.target.value)
                            .replace(/\D/g, "")
                            .slice(0, 9),
                        },
                      })
                    }
                    placeholder="حداکثر ۹ رقم"
                    className="w-full rounded-xl border px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel required>واحد</FieldLabel>
                  <Select
                    options={unitOptions}
                    value={
                      unitOptions.find((o) => o.value === item.unit) || null
                    }
                    onChange={(opt) =>
                      setInvoiceData((prev) => {
                        const items = [...prev.items];
                        items[index].unit = opt ? opt.value : "";
                        return { ...prev, items };
                      })
                    }
                    placeholder="انتخاب واحد"
                    {...selectTexts()}
                    styles={selectStyles}
                    isClearable
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel>وزن خالص (کیلوگرم)</FieldLabel>
                  <input
                    type="number"
                    step="0.01"
                    name="nw_kg"
                    value={item.nw_kg}
                    onChange={(e) => handleItemChange(index, e)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel>وزن ناخالص (کیلوگرم)</FieldLabel>
                  <input
                    type="number"
                    step="0.01"
                    name="gw_kg"
                    value={item.gw_kg}
                    onChange={(e) => handleItemChange(index, e)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel>تعداد بسته‌بندی</FieldLabel>
                  <input
                    type="number"
                    name="pack"
                    value={item.pack}
                    onChange={(e) => handleItemChange(index, e)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-1">
                  <FieldLabel required>مبدأ</FieldLabel>
                  <Select
                    isMulti
                    options={countryOptions}
                    value={
                      item.origin
                        ? item.origin
                            .split("-")
                            .map((v) =>
                              countryOptions.find((o) => o.value === v.trim())
                            )
                        : []
                    }
                    onChange={(opts) => {
                      const vals = opts ? opts.map((o) => o.value) : [];
                      setInvoiceData((prev) => {
                        const items = [...prev.items];
                        items[index].origin = vals.join("-");
                        return { ...prev, items };
                      });
                    }}
                    placeholder="انتخاب مبدأ"
                    {...selectTexts()}
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  جمع خطی:{" "}
                  <span className="font-semibold text-gray-900">
                    {(
                      (parseFloat(item.unit_price) || 0) *
                      (parseFloat(item.quantity) || 0)
                    ).toFixed(2)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeItemRow(index)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  حذف کالا
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary + Sticky actions */}
      <div className="sticky bottom-3 mt-6 z-10">
        <div className="mx-auto w-full md:w-auto md:ml-auto flex flex-col md:flex-row gap-2 md:justify-end bg-white/80 backdrop-blur rounded-2xl border p-3 shadow-sm">
          <div className="flex-1 md:flex-none md:px-3 md:py-2 rounded-xl border bg-gray-50 text-sm text-gray-700">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                جمع فرعی: <b>{subTotal.toFixed(2)}</b>
              </span>
              <span>
                تعداد کل: <b>{totalQty}</b>
              </span>
              <span>
                NW: <b>{totalNW}</b>
              </span>
              <span>
                GW: <b>{totalGW}</b>
              </span>
              <span>
                Pack: <b>{totalPack}</b>
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            <Save className="w-4 h-4" />
            ذخیره فاکتور
          </button>
        </div>
      </div>
    </form>
      {quickCreateType ? (
        <InvoicePartyQuickCreate
          type={quickCreateType}
          onClose={() => setQuickCreateType(null)}
          onCreated={handlePartyCreated}
        />
      ) : null}
    </>
  );
}
